// src/components/BulkUploadOpportunities.js
import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import Papa from "papaparse";

const BulkUploadOpportunities = ({ isOpen, onClose, onBulkUpload }) => {
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = () => {
    if (!file) {
      toast.error("Please select a CSV file.");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        console.log("Parsed CSV data:", results.data);

        const formattedData = results.data.map((opportunity) => {
          // Parse schedule data
          const schedule = {};
          if (
            opportunity.scheduleDays &&
            opportunity.scheduleStartTimes &&
            opportunity.scheduleEndTimes
          ) {
            const days = opportunity.scheduleDays.includes(",")
              ? opportunity.scheduleDays.split(",").map((day) => day.trim())
              : [opportunity.scheduleDays.trim()];

            const startTimes = opportunity.scheduleStartTimes.includes(",")
              ? opportunity.scheduleStartTimes
                  .split(",")
                  .map((time) => time.trim())
              : [opportunity.scheduleStartTimes.trim()];

            const endTimes = opportunity.scheduleEndTimes.includes(",")
              ? opportunity.scheduleEndTimes
                  .split(",")
                  .map((time) => time.trim())
              : [opportunity.scheduleEndTimes.trim()];

            days.forEach((day, index) => {
              if (startTimes[index] && endTimes[index]) {
                schedule[day] = {
                  startTime: startTimes[index],
                  endTime: endTimes[index],
                };
              }
            });
          }

          // Parse studentIds
          const studentIds = opportunity.studentIds
            ? opportunity.studentIds.split(";").map((id) => id.trim())
            : [];

          // Handle siteVisit conversion properly
          const siteVisit = opportunity.siteVisit.toLowerCase() === "true";

          // Handle dateOfVisit
          const dateOfVisit =
            opportunity.dateOfVisit && opportunity.dateOfVisit !== "null"
              ? opportunity.dateOfVisit
              : null;

          // Handle visitBy
          const visitBy =
            opportunity.visitBy && opportunity.visitBy !== "null"
              ? opportunity.visitBy
              : null;

          return {
            id: Math.floor(Math.random() * 1000) + 200,
            hostName: opportunity.hostName,
            hostPhoneNumber: opportunity.hostPhoneNumber,
            streetAddress: opportunity.streetAddress,
            city: opportunity.city,
            stateProvince: opportunity.stateProvince,
            postalCode: opportunity.postalCode,
            requiredDress: opportunity.requiredDress.toLowerCase(),
            schedule,
            opportunityType: opportunity.opportunityType,
            students: studentIds,
            siteVisit,
            dateOfVisit,
            visitBy,
          };
        });

        console.log("Formatted data:", formattedData);
        onBulkUpload(formattedData);
        setFile(null);
        onClose();
      },
      error: function (error) {
        console.error("CSV parsing error:", error);
        toast.error("Error parsing CSV file.");
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>
        <h2 id="modal-title" className="text-xl font-bold mb-4">
          Bulk Upload Opportunities
        </h2>
        <div className="space-y-4">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="w-full"
          />
          <p className="text-sm text-gray-500">
            <span className="mb-1 block">
              Please upload a CSV file with the following headers:
            </span>
            <code className="text-xs bg-gray-100 p-1 rounded break-all">
              hostName, hostPhoneNumber, streetAddress, city, stateProvince,
              postalCode, requiredDress, scheduleDays, scheduleStartTimes,
              scheduleEndTimes, opportunityType, studentIds, siteVisit,
              dateOfVisit, visitBy
            </code>
          </p>
          <p className="text-xs text-gray-400">
            Download our{" "}
            <a
              href="/sample_opportunities.csv"
              download
              className="text-blue-500 underline hover:text-blue-600"
            >
              sample file
            </a>{" "}
            for reference.
          </p>
          <p className="text-xs text-gray-400">
            Note: scheduleDays should be comma-separated (e.g.,
            "Monday,Wednesday,Friday"), scheduleStartTimes and scheduleEndTimes
            should match the days (e.g., "09:00,09:00,09:00" and
            "12:00,12:00,12:00"), and studentIds should be semicolon-separated
            (e.g., "S1001;S1002")
          </p>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadOpportunities;
